import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  MessageCircle, 
  Heart, 
  Repeat, 
  Share, 
  Filter, 
  Search,
  TrendingUp,
  Hash,
  Calendar,
  Users,
  RefreshCw,
  Wifi,
  WifiOff
} from 'lucide-react'
import { useStore } from '../store/useStore'
import { useTwitterMentions, useTrendingHashtags, useTwitterDataManager, useTwitterConnectionStatus, useFilteredTweets } from '../hooks/useTwitterData'
import { formatDistanceToNow } from 'date-fns'

const Community = () => {
  const [activeTab, setActiveTab] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  // Real Twitter data hooks
  const { data: tweets = [], isLoading: tweetsLoading, error: tweetsError } = useTwitterMentions(50)
  const { data: trendingHashtags = [], isLoading: hashtagsLoading } = useTrendingHashtags()
  const { isLoading: refreshing, lastUpdate, refreshData } = useTwitterDataManager()
  const { isConnected, connectionError } = useTwitterConnectionStatus()

  // Filter tweets based on active tab and search
  const filteredTweets = useFilteredTweets(tweets, activeTab, searchTerm)

  // Community stats (calculated from real data)
  const communityStats = [
    { 
      label: 'Total Mentions', 
      value: tweets.length.toLocaleString(), 
      change: '+12%', 
      icon: MessageCircle 
    },
    { 
      label: 'Active Users', 
      value: new Set(tweets.map(t => t.username)).size.toLocaleString(), 
      change: '+8%', 
      icon: Users 
    },
    { 
      label: 'Engagement Rate', 
      value: tweets.length > 0 ? 
        `${Math.round(tweets.reduce((sum, t) => sum + t.likes + t.retweets, 0) / tweets.length)}` : 
        '0', 
      change: '+3%', 
      icon: TrendingUp 
    },
    { 
      label: 'Daily Posts', 
      value: tweets.filter(t => 
        new Date(t.timestamp) > new Date(Date.now() - 24 * 60 * 60 * 1000)
      ).length.toString(), 
      change: '+18%', 
      icon: Calendar 
    }
  ]

  const handleLike = (tweetId: string) => {
    // Mock like functionality - in real app would call Twitter API
    console.log('Liked tweet:', tweetId)
  }

  const handleRetweet = (tweetId: string) => {
    // Mock retweet functionality
    console.log('Retweeted tweet:', tweetId)
  }

  const handleReply = (tweetId: string) => {
    // Mock reply functionality
    console.log('Reply to tweet:', tweetId)
  }

  const formatTimestamp = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
    } catch {
      return timestamp
    }
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Community</h1>
          <p className="text-dark-400 mt-1">Real-time social activity around @LunaticsBTC</p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Connection Status */}
          <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
            isConnected ? 'bg-green-500/20' : 'bg-red-500/20'
          }`}>
            {isConnected ? (
              <Wifi className="w-4 h-4 text-green-400" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-400" />
            )}
            <span className={`text-sm ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
              {isConnected ? 'Connected' : 'Offline'}
            </span>
          </div>
          
          {/* Refresh Button */}
          <button
            onClick={refreshData}
            disabled={refreshing}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Last Update Info */}
      {lastUpdate && (
        <div className="text-sm text-dark-400">
          Last updated: {formatTimestamp(lastUpdate.toISOString())}
        </div>
      )}

      {/* Connection Error */}
      {connectionError && (
        <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4">
          <p className="text-red-400 text-sm">
            Connection Error: {connectionError}
          </p>
          <p className="text-red-400/80 text-xs mt-1">
            Falling back to cached data. Check your API configuration.
          </p>
        </div>
      )}

      {/* Community Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {communityStats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-dark-400">{stat.label}</p>
                <p className="text-xl font-bold text-white mt-1">{stat.value}</p>
                <p className="text-sm font-medium text-green-400 mt-1">{stat.change}</p>
              </div>
              <div className="p-3 rounded-full bg-lunatic-500/10">
                <stat.icon className="w-5 h-5 text-lunatic-400" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Feed */}
        <div className="lg:col-span-2 space-y-6">
          {/* Filters */}
          <div className="card">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <div className="flex items-center space-x-2">
                {['all', 'mentions', 'hashtags', 'trending'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeTab === tab
                        ? 'bg-lunatic-500 text-white'
                        : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
                    }`}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search tweets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-primary pl-10 w-64"
                />
              </div>
            </div>
          </div>

          {/* Loading State */}
          {tweetsLoading && (
            <div className="card">
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-lunatic-400 animate-spin" />
                <span className="ml-3 text-lunatic-400">Loading latest tweets...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {tweetsError && (
            <div className="card">
              <div className="text-center py-12">
                <p className="text-red-400 mb-2">Failed to load tweets</p>
                <button onClick={refreshData} className="btn-primary">
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Tweet Feed */}
          {!tweetsLoading && !tweetsError && (
            <div className="space-y-4">
              {filteredTweets.length === 0 ? (
                <div className="card">
                  <div className="text-center py-12">
                    <MessageCircle className="w-12 h-12 text-dark-400 mx-auto mb-4" />
                    <p className="text-dark-400">No tweets found matching your criteria</p>
                    <p className="text-dark-500 text-sm mt-1">Try adjusting your filters or search term</p>
                  </div>
                </div>
              ) : (
                filteredTweets.map((tweet, index) => (
                  <motion.div
                    key={tweet.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="card card-hover"
                  >
                    <div className="flex space-x-4">
                      <img
                        src={tweet.avatar}
                        alt={tweet.username}
                        className="w-12 h-12 rounded-full"
                        onError={(e) => {
                          e.currentTarget.src = '/api/placeholder/40/40'
                        }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="font-semibold text-white">
                            {tweet.name || tweet.username}
                          </span>
                          <span className="text-sm text-dark-400">
                            @{tweet.username}
                          </span>
                          {tweet.isVerified && (
                            <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          )}
                          <span className="text-sm text-dark-400">
                            {formatTimestamp(tweet.timestamp)}
                          </span>
                        </div>
                        <p className="text-dark-300 mb-4 leading-relaxed">{tweet.content}</p>
                        
                        {/* Hashtags */}
                        {tweet.hashtags && tweet.hashtags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {tweet.hashtags.map(hashtag => (
                              <span
                                key={hashtag}
                                className="px-2 py-1 bg-lunatic-500/10 text-lunatic-400 rounded-full text-xs"
                              >
                                {hashtag}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center space-x-6">
                          <button
                            onClick={() => handleReply(tweet.id)}
                            className="flex items-center space-x-2 text-dark-400 hover:text-primary-400 transition-colors"
                          >
                            <MessageCircle className="w-4 h-4" />
                            <span className="text-sm">{tweet.replies}</span>
                          </button>
                          <button
                            onClick={() => handleRetweet(tweet.id)}
                            className="flex items-center space-x-2 text-dark-400 hover:text-green-400 transition-colors"
                          >
                            <Repeat className="w-4 h-4" />
                            <span className="text-sm">{tweet.retweets}</span>
                          </button>
                          <button
                            onClick={() => handleLike(tweet.id)}
                            className="flex items-center space-x-2 text-dark-400 hover:text-red-400 transition-colors"
                          >
                            <Heart className="w-4 h-4" />
                            <span className="text-sm">{tweet.likes}</span>
                          </button>
                          <button className="flex items-center space-x-2 text-dark-400 hover:text-lunatic-400 transition-colors">
                            <Share className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Trending Hashtags */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="card"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Trending Hashtags</h3>
              {hashtagsLoading && (
                <RefreshCw className="w-4 h-4 text-lunatic-400 animate-spin" />
              )}
            </div>
            <div className="space-y-3">
              {trendingHashtags.map((hashtag, index) => (
                <div key={hashtag.tag} className="flex items-center justify-between p-3 bg-dark-800/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-lunatic-500/20">
                      <Hash className="w-3 h-3 text-lunatic-400" />
                    </div>
                    <span className="text-sm font-medium text-white">{hashtag.tag}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-white">{hashtag.count}</p>
                    <p className="text-xs text-green-400">{hashtag.change}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* API Configuration Help */}
          {!isConnected && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="card bg-blue-500/10 border border-blue-500/20"
            >
              <h3 className="text-lg font-semibold text-white mb-2">Setup Twitter API</h3>
              <p className="text-blue-400 text-sm mb-4">
                To see real Twitter data, configure your API keys in the .env file.
              </p>
              <div className="space-y-2 text-xs text-blue-300">
                <p>1. Get API keys from developer.twitter.com</p>
                <p>2. Add VITE_TWITTER_BEARER_TOKEN to .env</p>
                <p>3. Set VITE_ENABLE_REAL_DATA=true</p>
                <p>4. Restart the development server</p>
              </div>
            </motion.div>
          )}

          {/* Community Guidelines */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="card"
          >
            <h3 className="text-lg font-semibold text-white mb-4">Community Guidelines</h3>
            <div className="space-y-3 text-sm text-dark-300">
              <p>• Use @LunaticsBTC to join the conversation</p>
              <p>• Share quality content and insights</p>
              <p>• Be respectful to all community members</p>
              <p>• No spam or promotional content</p>
              <p>• Help newcomers learn about Ordinals</p>
            </div>
          </motion.div>

          {/* Weekly Challenge */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="card bg-lunatic-gradient"
          >
            <h3 className="text-lg font-semibold text-white mb-2">Weekly Challenge</h3>
            <p className="text-white/90 text-sm mb-4">
              Share your favorite Lunatic and tag 3 friends to earn bonus social points!
            </p>
            <div className="flex items-center justify-between">
              <span className="text-white/80 text-xs">Ends in 3 days</span>
              <button className="bg-white/20 text-white px-3 py-1 rounded-lg text-sm hover:bg-white/30 transition-colors">
                Participate
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Community 